import _ from 'lodash';
import { injectable } from '../shared/inversify';
import { Emitter, type Event } from '../common/event';

/**
 * Represents a base model with common properties and methods.
 *
 * @template Data - The type of the model data.
 */
export interface Model<Data = unknown> {
    /**
     * The model data.
     */
    readonly modelData: Data;

    /**
     * Event that is triggered when the model states change.
     */
    readonly onStatesChange: Event<Data>;

    /**
     * Updates the model data using the provided updater function.
     *
     * @param updater - A function that takes the current value of the model data and returns the updated value.
     */
    update: (updater: (value: Data) => Data) => void;

    /**
     *  Clones the model data.
     * @param data - The model data to clone.
     * @returns A clone of the model data.
     */
    clone: (data: Data) => Data;
}

/**
 * Abstract base class for models.
 * @template Data - The type of the model data.
 */
@injectable()
export abstract class AbstractModel<Data = unknown> implements Model<Data> {
    /**
     * The model data.
     * @description default state, initial state, that is used to initialize the model data.
     */
    protected abstract _modelData: Data;

    /**
     * Emitter for states change events.
     */
    private _onStatesChangeEmitter = new Emitter<Data>();

    /**
     * Event that fires when the states change.
     */
    public readonly onStatesChange = this._onStatesChangeEmitter.event;

    /**
     * Clones the model data.
     * @returns A deep clone of the model data.
     */
    clone(data: Data) {
        return _.cloneDeep(data) as Data;
    }

    /**
     * Gets the model data.
     * @returns The model data.
     */
    get modelData(): Data {
        return this.clone(this._modelData);
    }

    /**
     * Emits the model data change event.
     */
    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.modelData);
    }

    /**
     * Updates the model data.
     * @param updater - A function that takes the current model data and returns the updated model data.
     */
    public update(updater: (oldData: Data) => Data) {
        const newData = updater(this.modelData);
        this._update(newData);
        this._emitModelDataChange();
    }

    /**
     * Updates the model data.
     * @param value - The updated model data.
     */
    private _update(value: Data) {
        this._modelData = value;
    }
}

